import { useEffect, useState } from 'react';
import { useChatContext } from 'src/components/Chat/Chat.hook';
import { useModalsContext } from 'src/providers/ModalsProvider/Modals.context';
import { eChannelType } from 'src/providers/SocketsProvider/Sockets.enums';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import {
  buildTriggerAt,
  evaluateSchedulingRisk,
  isEligibilityHandle,
} from './SchedulingRiskWarning.functions';
import { SchedulingRiskService } from './SchedulingRiskWarning.service';
import type {
  tMarketingEligibility,
  tSchedulingContent,
  tSchedulingRisk,
} from './SchedulingRiskWarning.types';

export type tUseSchedulingRiskOptions = {
  triggerDate: string | undefined;
  triggerTime: string | undefined;
  content: tSchedulingContent;
};

export type tUseSchedulingRisk = {
  risk: tSchedulingRisk | null;
  triggerAt: Date | null;
  /** The lead's name for the copy, or null when it is not a name to show. */
  leadName: string | null;
};

type tEligibilityState = {
  key: string;
  eligibility: tMarketingEligibility | null;
};

/**
 * INT-1500 U13 (D6, D16): the warning for what the seller is scheduling, in a
 * WhatsApp API chat. Reads the lead's marketing eligibility once per lead and
 * evaluates it against the chosen time and content on every render.
 *
 * Fails open: outside a WhatsApp API chat, for a handle the route cannot take,
 * while the read is in flight and when it fails, there is no warning and the
 * modal behaves exactly as today.
 */
export default function useSchedulingRisk(
  options: tUseSchedulingRiskOptions,
): tUseSchedulingRisk {
  const { triggerDate, triggerTime, content } = options;
  const { currentWorkspaceId } = useWorkspaceContext();
  const { selectedLeadForChatModal } = useModalsContext();
  const { selectedChat } = useChatContext();

  const integration =
    selectedChat?.integration ?? selectedLeadForChatModal?.chat?.integration;
  const handle = selectedLeadForChatModal?.handle;
  const canAsk =
    integration === eChannelType.WHATSAPP_API &&
    Boolean(currentWorkspaceId) &&
    isEligibilityHandle(handle);
  const key = canAsk ? `${currentWorkspaceId}:${handle}` : null;

  const [state, setState] = useState<tEligibilityState | null>(null);

  useEffect(() => {
    if (!key || !currentWorkspaceId || !handle) return undefined;
    const controller = new AbortController();

    SchedulingRiskService.getMarketingEligibility(
      { workspaceId: currentWorkspaceId, handle },
      { signal: controller.signal },
    )
      .then((eligibility) => {
        if (!controller.signal.aborted) setState({ key, eligibility });
      })
      .catch(() => {
        if (!controller.signal.aborted) setState({ key, eligibility: null });
      });

    return () => controller.abort();
  }, [key]);

  const eligibility = key && state?.key === key ? state.eligibility : null;
  const triggerAt = buildTriggerAt(triggerDate, triggerTime);
  const risk = evaluateSchedulingRisk({
    eligibility,
    triggerAt,
    content,
    now: new Date(),
  });

  const name = selectedLeadForChatModal?.name?.trim();
  const leadName = name && !name.includes('@lid') ? name : null;

  return { risk, triggerAt, leadName };
}
